import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import {
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup
} from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import { Check } from '@mui/icons-material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SaveIcon from '@mui/icons-material/Save'

import { getAccounts, deleteAccount, updateAccount } from '../../../actions'
import { Logger, LoggerTypes, checkErrorResponse, checkUpdateResponse } from '../../../utils'
import { Button, buttonModes, buttonTypes, FormTitle, DialogConfirm, AccessControl } from '../../../components'
import { userRightTypes, userRoleTypes } from '../../../constants'
import { setUserRightsToStorage } from '../../../storage/storage'
import { UserCreateDialog } from '../UserCreateDialog'
import { Close } from '@mui/icons-material'
import { getUserInfosFromStorage } from '../../../storage/storage'
import './style.scss'

const useStyles = makeStyles((theme) =>
  createStyles({
    plusStyle: {
      border: `1px solid ${theme.palette.primary.main}`
    },
    plusIconStyle: {
      color: theme.palette.primary.main
    }
  })
)

export const UserRightsDialog = ({ setOpenDialog, className }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const account = useSelector((state) => state.account) || {}
  const styleClasses = useStyles()

  const [openUserCreateDialog, setOpenUserCreateDialog] = useState(false)
  const [openUserDeleteDialog, setOpenUserDeleteDialog] = useState(false)
  const [users, setUsers] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [userInfos] = useState(getUserInfosFromStorage())

  // -- sort users by ID to prevents the reordering after saving.
  if (users) {
    users.sort((a, b) => (a.id > b.id ? 1 : -1))
  }

  const checkLastAdmin = () => {
    let count = 0
    let userAdmin
    users.map((user) => {
      if (user.role === 'ADMINISTRATOR') {
        userAdmin = user
        count++
      }
    })
    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectDialogDelete.checkLastAdmin()', count, users, currentUser)
    if (count <= 1 && currentUser === userAdmin) {
      return true
    }
    return false
  }

  const loadUsers = () => {
    dispatch(getAccounts())
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'UserRightsDialog.dispatch(getAccounts()).then()', { res })
        setUsers(res.data)
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'UserRightsDialog.dispatch(getAccounts()).catch()', { res })
        checkErrorResponse(res, enqueueSnackbar, t)
      })
  }

  useEffect(() => {
    loadUsers()
  }, [])

  const onCloseDialog = () => setOpenDialog(false)

  const onUserCreate = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'UserRightsDialog.onUserCreate()', { openUserCreateDialog })

    setOpenUserCreateDialog(!openUserCreateDialog)
  }

  const onUserSelected = (_user) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'UserRightsDialog.onUserSelected()', { currentUser: _user })

    setCurrentUser(_user)
  }

  const onUserRoleChange = (_role) => {
    const newUser = {
      ...currentUser,
      role: _role
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'UserRightsDialog.onUserRoleChange()', { newUser })

    setCurrentUser(newUser)
    setUserRightsToStorage(null)
  }

  const onDialogConfirmDelete = (isDelete) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'UserRightsDialog.onDialogConfirmDelete()', { currentUser, isDelete })

    if (isDelete && currentUser && Boolean(currentUser.id)) {
      dispatch(deleteAccount(currentUser.id))
        .then((res) => {
          Logger.trace(
            LoggerTypes.CONTROLLER,
            'UserRightsDialog.onDialogConfirmDelete().dispatch(deleteAccounts()).then()',
            { res }
          )
          setOpenUserDeleteDialog(!openUserDeleteDialog)
          loadUsers()
        })
        .catch((res) => {
          Logger.trace(
            LoggerTypes.WARN,
            'UserRightsDialog.onDialogConfirmDelete().dispatch(deleteAccounts()).catch()',
            { res }
          )
          checkErrorResponse(res, enqueueSnackbar, t)
        })
    }
  }

  const onUserUpdate = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'UserRightsDialog.onUserUpdate()', { currentUser })

    dispatch(updateAccount(currentUser))
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'UserRightsDialog.onUserUpdate().dispatch(updateAccount()).then()', {
          res
        })
        // setOpenDialog(false)  // <-- should we close the popin after a successful save?
        loadUsers()
        checkUpdateResponse(res, enqueueSnackbar, t)
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'UserRightsDialog.onUserUpdate().dispatch(updateAccount()).catch()', { res })
        checkErrorResponse(res, enqueueSnackbar, t)
      })
  }

  const onClickCancel = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectDialogDelete.onClickCancel()')
    setOpenDialog(false)
  }

  Logger.trace(LoggerTypes.CONTAINER, 'UserRightsDialog.render()', { account, currentUser, users })

  return (
    <Dialog
      open={true}
      onClose={onCloseDialog} // <-- close while clicking on background
      maxWidth="xl"
      width="xl"
      className={`UserRightsDialog ${className}`}
    >
      <DialogContent>
        <FormTitle title={t('users.rights.title')} style={{ fontSize: '20px' }} />
        <Button
          mode={buttonModes.SECONDARY_ACTION}
          type={buttonTypes.BUTTON}
          tooltip={t('common.close')}
          icon={<CloseIcon className={styleClasses.plusIconStyle} />}
          onClick={onCloseDialog}
          className={`headerButton ${styleClasses.plusStyle}`}
        />
        <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }}>
          {/* === COLUMN 01 : Users =========================================================================== */}
          <Grid item xs={0} sm={0} md={5} className="column columnUser">
            <AccessControl
              userPermissions={[userRightTypes.USER_RIGHTS_READ]}
              renderNoAccess={() => <div className="noResult">{t('users.rights.no_right')}</div>}
            >
              <MenuList dense>
                {users &&
                  users.map &&
                  users.map((user, index) => (
                    <MenuItem
                      key={`operator-${index}`}
                      onClick={() => onUserSelected(user)}
                      className={currentUser && currentUser.id === user?.id ? 'lineSelected' : ''}
                    >
                      <ListItemIcon>
                        {currentUser && currentUser.id === user?.id ? <Check checked /> : null}
                      </ListItemIcon>
                      <ListItemText inset className="name">
                        {user.email} {user.name && <i>({user.name})</i>}
                      </ListItemText>
                    </MenuItem>
                  ))}
              </MenuList>
            </AccessControl>
          </Grid>

          {/* === COLUMN 02 : User's Roles ================================================================ */}
          <Grid item xs={0} sm={0} md={2} className="column columnRoles">
            <>
              <FormControl>
                <RadioGroup defaultValue={userRoleTypes.USER}>
                  {Object.keys(userRoleTypes).map((role, index) => (
                    <FormControlLabel
                      key={`role-${index}`}
                      value={role}
                      control={
                        <Radio
                          disabled={!currentUser || checkLastAdmin(currentUser)}
                          checked={currentUser ? role === currentUser.role : false}
                          onClick={() => onUserRoleChange(role)}
                        />
                      }
                      label={t(`users.rights.role.types.${role}`)}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <section className="userFooterButtons">
                <AccessControl userPermissions={[userRightTypes.USER_RIGHTS_DELETE]} renderNoAccess={() => <></>}>
                  <Button
                    disabled={!currentUser || currentUser.id === userInfos.id || checkLastAdmin(currentUser)}
                    mode={buttonModes.SECONDARY_ACTION}
                    type={buttonTypes.BUTTON}
                    text={t('common.delete')}
                    icon={<DeleteOutlineIcon />}
                    onClick={() => setOpenUserDeleteDialog(!openUserDeleteDialog)}
                    className="common_halfwidth button delete"
                  />
                </AccessControl>
                <AccessControl userPermissions={[userRightTypes.USER_RIGHTS_EDIT]} renderNoAccess={() => <></>}>
                  <Button
                    disabled={!currentUser || checkLastAdmin(currentUser)}
                    mode={buttonModes.PRIMARY_ACTION}
                    type={buttonTypes.BUTTON}
                    text={t('common.save')}
                    icon={<SaveIcon />}
                    onClick={onUserUpdate}
                    className="common_halfwidth button"
                  />
                </AccessControl>
              </section>
            </>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <AccessControl userPermissions={[userRightTypes.USER_RIGHTS_EDIT]} renderNoAccess={() => <></>}>
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            type={buttonTypes.BUTTON}
            text={t('users.rights.create')}
            className="common_halfwidth button"
            icon={<AddIcon />}
            onClick={onUserCreate}
          />
          <Button
            mode={buttonModes.SECONDARY_ACTION}
            type={buttonTypes.BUTTON}
            text={t('common.cancel')}
            icon={<Close />}
            onClick={onClickCancel}
          />
        </AccessControl>
      </DialogActions>
      {openUserCreateDialog && <UserCreateDialog setOpenDialog={onUserCreate} onSave={loadUsers} />}
      {openUserDeleteDialog && (
        <DialogConfirm
          openDialog={openUserDeleteDialog}
          setOpenDialog={setOpenUserDeleteDialog}
          onClose={onDialogConfirmDelete}
          title={`${t('users.rights.delete.title')}: ${currentUser?.email || ''}`}
          message={t('users.rights.delete.message')}
        />
      )}
    </Dialog>
  )
}

UserRightsDialog.displayName = 'UserRightsDialog'

UserRightsDialog.propTypes = {
  setOpenDialog: PropTypes.func.isRequired,
  className: PropTypes.string
}

UserRightsDialog.defaultProps = {
  setOpenDialog: () => {},
  className: ''
}
